import { useFileUpload } from "@msuite/ui/file-upload";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal } from "@msuite/ui/modal";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { moment } from "@msuite/utils/date";
import { type ProjectTimelineNoteSchema, projectTimelineNoteSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { useUpload } from "@/api/files";
import { ProjectTimelineNoteForm } from "@/forms/project-timeline-note-form";
import { client } from "@/hono";
import { useExistingFileController } from "@/hooks/use-existing-file-controller";

/** Props Interface */
interface AddProjectTimelineNoteModalProps {
	isOpen: boolean;
	onClose: () => void;
	projectId: string;
	onComplete: () => Promise<void>;
}

export function AddProjectTimelineNoteModal({
	isOpen,
	onClose,
	projectId,
	onComplete,
}: AddProjectTimelineNoteModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { uploadFn } = useUpload();
	const controller = useFileUpload({
		uploadFn,
	});
	const existingFileController = useExistingFileController();
	const methods = useForm({
		defaultValues: {
			date: moment().db(),
			isImportant: true,
		},
		resolver: resolver(projectTimelineNoteSchema),
	});

	/** Functions */
	async function handleOnSubmit(data: ProjectTimelineNoteSchema) {
		try {
			const uploadResults = await controller.handleOnFileUpload();
			const response = await client.projects[":projectId"].timeline.note.$post({
				param: { projectId },
				json: {
					...data,
					uploadResults,
					existingFileIds: existingFileController.pendingFileIds,
				},
			});
			// Without this the entry closes on a refusal and the modal reports a note nobody wrote.
			await responseHandler(response);
			await onComplete();
			onClose();
			controller.handleOnReset();
			existingFileController.handleOnReset();
			methods.reset();
		} catch (error) {
			console.error("Error adding note:", error);
			toast.error(t("general.something_went_wrong"));
		}
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				onClose={onClose}
				isOpen={isOpen}
				title={t("notes.add_note")}
				width="700px"
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				data-cy="project-timeline-note-form"
			>
				<Modal.Body>
					<ProjectTimelineNoteForm
						controller={controller}
						projectId={projectId}
						existingFileController={existingFileController}
					/>
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
